// pages/history.tsx
import { FC, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSpotifyPlayback } from '../contexts/SpotifyPlaybackContext';
import PlaybackStatusMessage from "../components/PlaybackStatusMessage";

interface RecentlyPlayedItem {
  trackId: string;
  trackName: string;
  trackUri: string;
  duration_ms: number;
  playedAt: string;
  album: {
    id: string;
    name: string;
    artist: string;
    image_url: string;
    spotify_album_id: string;
  };
  artists: string;
  context: { type: string; uri: string; } | null;
}

const HistoryPage: FC = () => {
  const { accessToken, isAuthenticated, login } = useAuth();
  const { playAlbum, playTrackInContext } = useSpotifyPlayback();
  const [recentHistory, setRecentHistory] = useState<RecentlyPlayedItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [groupingMode, setGroupingMode] = useState<'artist' | 'album' | 'track'>('artist');
  const [playbackStatuses, setPlaybackStatuses] = useState<{[key: string]: string | null}>({});

  useEffect(() => {
    if (!isAuthenticated || !accessToken) return;

    const fetchData = async () => {
      try {
        const response = await axios.get<RecentlyPlayedItem[]>('/api/spotify/recently-played', { params: { access_token: accessToken } });
        setRecentHistory(response.data);
      } catch (error) {
        console.error('Error fetching recent Spotify history:', error);
      }
      setLoading(false);
    };

    fetchData();
  }, [isAuthenticated, accessToken]);

  const handlePlayAlbum = async (album: any) => {
    setPlaybackStatuses(prev => ({ ...prev, [album.id]: null }));

    if (accessToken) {
      const result = await playAlbum(album.spotify_album_id);
      
      if (result.success) {
        setPlaybackStatuses(prev => ({ ...prev, [album.id]: "Now playing" }));
      } else {
        setPlaybackStatuses(prev => ({ ...prev, [album.id]: `Error: ${result.message}` }));
      }
    }
  };

  const handleResumeTrack = async (item: RecentlyPlayedItem) => {
    setPlaybackStatuses(prev => ({ ...prev, [item.trackId]: null }));

    if (accessToken) {
      const albumUri = `spotify:album:${item.album.spotify_album_id}`;
      const result = await playTrackInContext({
        trackUri: item.trackUri,
        albumUri: albumUri,
      });

      if (result.success) {
        setPlaybackStatuses(prev => ({ ...prev, [item.trackId]: "Now playing" }));
      } else {
        setPlaybackStatuses(prev => ({ ...prev, [item.trackId]: `Error: ${result.message}` }));
      }
    }
  };

  if (!isAuthenticated) {
    return (
        <div className="flex flex-col items-center justify-center min-h-screen p-4">
          <h1 className="text-3xl font-bold mb-8 text-center">Listening History</h1>
          <p className="text-lg mb-8 text-center">
            Please login to view your listening history and statistics.
          </p>
          <button
              onClick={login}
              className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-full text-lg transition duration-300"
          >
            Login with Spotify
          </button>
        </div>
    );
  }

  type HistoryGroup = {
    type: 'album-group' | 'artist-group';
    album: RecentlyPlayedItem['album'];
    artist?: string;
    tracks: RecentlyPlayedItem[];
    playedAt: string;
  };
  const groupedRecentHistory: { [date: string]: (RecentlyPlayedItem | HistoryGroup)[] } = {};
  const dateKey = (value: string) => {
    const date = new Date(value);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };
  
  if (groupingMode === 'track') {
    recentHistory.forEach(item => {
      const date = dateKey(item.playedAt);
      if (!groupedRecentHistory[date]) {
        groupedRecentHistory[date] = [];
      }
      groupedRecentHistory[date].push(item);
    });
  } else {
    // Group by album or artist within each day.
    const dayGroups: { [date: string]: { [groupId: string]: RecentlyPlayedItem[] } } = {};
    
    recentHistory.forEach(item => {
      const date = dateKey(item.playedAt);
      if (!dayGroups[date]) dayGroups[date] = {};
      const groupId = groupingMode === 'artist' ? item.artists : item.album.id;
      if (!dayGroups[date][groupId]) dayGroups[date][groupId] = [];
      dayGroups[date][groupId].push(item);
    });
    
    Object.entries(dayGroups).forEach(([date, groups]) => {
      groupedRecentHistory[date] = Object.values(groups).map(groupTracks => {
        const tracks = [...groupTracks].sort((a, b) => new Date(b.playedAt).getTime() - new Date(a.playedAt).getTime());
        return {
          type: groupingMode === 'artist' ? 'artist-group' as const : 'album-group' as const,
          album: tracks[0].album,
          artist: tracks[0].artists,
          tracks,
          playedAt: tracks[0].playedAt
        };
      });
      // Re-sort by playedAt descending within the date because Object.values might not preserve order
      groupedRecentHistory[date].sort((a, b) => new Date(b.playedAt).getTime() - new Date(a.playedAt).getTime());
    });
  }

  return (
      <div className="page-container compact-view">
        {loading ? (
            <p className="text-center py-8">Loading your listening data...</p>
        ) : (
            <div>
                    <div className="flex flex-col md:flex-row justify-between md:items-center mb-3 gap-2">
                      <div className="grid grid-cols-3 bg-gray-100 p-1 rounded-lg w-full md:w-auto">
                        <button
                          onClick={() => setGroupingMode('artist')}
                          className={`px-3 py-1 rounded-md text-sm transition ${
                            groupingMode === 'artist'
                              ? 'bg-white shadow-sm text-blue-600 font-medium'
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          By Artist
                        </button>
                        <button
                          onClick={() => setGroupingMode('album')}
                          className={`px-4 py-1 rounded-md text-sm transition ${
                            groupingMode === 'album'
                              ? 'bg-white shadow-sm text-blue-600 font-medium'
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          By Album
                        </button>
                        <button
                          onClick={() => setGroupingMode('track')}
                          className={`px-4 py-1 rounded-md text-sm transition ${
                            groupingMode === 'track'
                              ? 'bg-white shadow-sm text-blue-600 font-medium'
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          Tracks
                        </button>
                      </div>
                    </div>
                    
                    {recentHistory.length === 0 ? (
                        <div className="text-center py-8">
                          <p>No recent listening history found.</p>
                          <p className="text-gray-600 mt-2">Start playing music on Spotify to see your history here.</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                          {Object.entries(groupedRecentHistory).map(([date, items]) => (
                              <div key={date} className="compact-panel p-0 overflow-hidden">
                                <h2 className="font-bold px-3 py-2 bg-gray-100 border-b">{formatHistoryDate(date)}</h2>
                                <div className="divide-y">
                                  {items.map((item, idx) => {
                                    if ('type' in item && (item.type === 'album-group' || item.type === 'artist-group')) {
                                      const isArtistGroup = item.type === 'artist-group';
                                      return (
                                        <div key={`${isArtistGroup ? item.artist : item.album.id}-${item.playedAt}`} className="flex items-center gap-2 px-3 py-2">
                                          <div className="flex-grow min-w-0">
                                            <h3 className="font-semibold">{isArtistGroup ? item.artist : item.album.name}</h3>
                                            <p className="text-gray-700">
                                              {isArtistGroup
                                                ? `${new Set(item.tracks.map(track => track.album.name)).size} album${new Set(item.tracks.map(track => track.album.name)).size === 1 ? '' : 's'}`
                                                : item.album.artist}
                                            </p>
                                            <p className="text-xs text-gray-500">{item.tracks.length} track{item.tracks.length > 1 ? 's' : ''} · {new Date(item.playedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</p>
                                          </div>
                                          <div className="flex flex-col gap-2 self-stretch sm:self-auto">
                                            <div className="flex gap-2">
                                              <button
                                                  onClick={() => handleResumeTrack(item.tracks[0])}
                                                  className="compact-button bg-green-500 hover:bg-green-600 text-white px-3 rounded text-sm"
                                              >
                                                Resume
                                              </button>
                                            </div>
                                            <PlaybackStatusMessage status={playbackStatuses[item.tracks[0].trackId]} />
                                          </div>
                                        </div>
                                      );
                                    }
                                    
                                    const trackItem = item as RecentlyPlayedItem;
                                    return (
                                      <div key={`${trackItem.trackId}-${trackItem.playedAt}`} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 px-3 py-2">
                                        <div className="flex-grow min-w-0">
                                          <h3 className="font-semibold">{trackItem.trackName}</h3>
                                          <p className="text-sm text-gray-600 truncate">{trackItem.artists} · {trackItem.album.name}</p>
                                          <p className="text-xs text-gray-500">{new Date(trackItem.playedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</p>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                          <div className="flex gap-1">
                                            <button
                                                onClick={() => handleResumeTrack(trackItem)}
                                                className="compact-button bg-green-500 hover:bg-green-600 text-white px-2 rounded text-sm"
                                            >
                                              Resume
                                            </button>
                                            <button
                                                onClick={() => handlePlayAlbum(trackItem.album)}
                                                className="compact-button bg-blue-500 hover:bg-blue-600 text-white px-2 rounded text-sm"
                                            >
                                              Play Album
                                            </button>
                                          </div>
                                          <PlaybackStatusMessage 
                                            status={playbackStatuses[trackItem.trackId] || playbackStatuses[trackItem.album.id]} 
                                          />
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                          ))}
                        </div>
                    )}
            </div>
        )}
      </div>
  );
};

function formatHistoryDate(dateKey: string): string {
  const [year, month, day] = dateKey.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    ...(year !== new Date().getFullYear() ? { year: 'numeric' as const } : {}),
  }).format(date);
}

export default HistoryPage;
